import { Box, Typography, useTheme, IconButton } from "@mui/material";
import { useSelector } from "react-redux";
import { useState } from "react";

import EditOutlinedIcon  from "@mui/icons-material/EditOutlined";
import BusinessIcon      from "@mui/icons-material/Business";
import ImageIcon         from "@mui/icons-material/Image";

import FixedSizeDialog        from "components/fixedSizeDialog";
import BusinessDetailEditForm from "./businessDetailsEdit";
import BusinessLogoEditForm   from "./businessLogoEdit";
import { useGetBusiness }     from "api/hooks/business";

const Field = ({ label, value, textPri, textSec }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", py: "0.6rem", borderBottom: "0.5px solid rgba(29,158,117,0.08)" }}>
    <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: textSec }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "14px", color: textPri }}>
      {value || "—"}
    </Typography>
  </Box>
);

const BusinessSetupViewForm = ({ setIsSuccess, setIsFailed }) => {
  const theme   = useTheme();
  const isDark  = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_id = useSelector((s) => s.authReducer.business_id);
  const { business, setNewBusinessData } = useGetBusiness(business_id);

  const [openLogo,   setOpenLogo]   = useState(false);
  const [openDetail, setOpenDetail] = useState(false);

  const business_name   = business?.business_name    ?? "";
  const business_slogan = business?.business_slogan  ?? "";
  const picture         = business?.business_logo    ?? "";
  const email           = business?.email            ?? "";
  const address         = business?.business_location ?? "";

  const cardSx = {
    backgroundColor: cardBg,
    border: `1px solid ${border}`,
    borderRadius: "12px",
    p: "1.25rem 1.5rem",
    display: "flex", flexDirection: "column", gap: "0",
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── Page heading ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <Box sx={{
          width: "40px", height: "40px", borderRadius: "10px",
          backgroundColor: "rgba(29,158,117,0.12)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: accent, flexShrink: 0,
        }}>
          <BusinessIcon sx={{ fontSize: "22px" }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
            Business Setup
          </Typography>
          <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
            Manage your business profile and branding
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-start" }}>

        {/* ── Logo card ── */}
        <Box sx={{ ...cardSx, flex: "0 0 240px" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "1rem" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Box sx={{
                width: "30px", height: "30px", borderRadius: "8px",
                backgroundColor: "rgba(29,158,117,0.12)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: accent,
              }}>
                <ImageIcon sx={{ fontSize: "16px" }} />
              </Box>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>Business Logo</Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setOpenLogo(true)}
              sx={{
                width: "30px", height: "30px", borderRadius: "8px",
                border: `1px solid ${border}`,
                color: textSec,
                "&:hover": { backgroundColor: "rgba(29,158,117,0.08)", color: accent },
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: "15px" }} />
            </IconButton>
          </Box>

          {picture ? (
            <Box sx={{
              width: "100%", height: "120px",
              borderRadius: "8px",
              border: `1px solid ${border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              overflow: "hidden",
              backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#f9f9f9",
            }}>
              <img
                alt="Business Logo"
                src={`http://localhost:8000${picture}`}
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              />
            </Box>
          ) : (
            <Box sx={{
              width: "100%", height: "120px",
              borderRadius: "8px",
              border: `1px dashed ${border}`,
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: "6px",
              backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#f9f9f9",
            }}>
              <ImageIcon sx={{ fontSize: "28px", color: textSec, opacity: 0.4 }} />
              <Typography sx={{ fontSize: "11px", color: textSec }}>No logo uploaded</Typography>
            </Box>
          )}

          <FixedSizeDialog
            dialogHeading="Edit Business Logo"
            open={openLogo}
            handleClickOpen={() => setOpenLogo(true)}
            handleClose={() => setOpenLogo(false)}
            negativeResponse="Cancel"
            maxWidth="md"
            content={
              <Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}>
                <BusinessLogoEditForm
                  setIsSuccess={setIsSuccess}
                  setOpen={setOpenLogo}
                  setNewBusinessData={setNewBusinessData}
                />
              </Box>
            }
          />
        </Box>

        {/* ── Details card ── */}
        <Box sx={{ ...cardSx, flex: "1 1 320px" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "1rem" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Box sx={{
                width: "30px", height: "30px", borderRadius: "8px",
                backgroundColor: "rgba(29,158,117,0.12)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: accent,
              }}>
                <BusinessIcon sx={{ fontSize: "16px" }} />
              </Box>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>Business Details</Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setOpenDetail(true)}
              sx={{
                width: "30px", height: "30px", borderRadius: "8px",
                border: `1px solid ${border}`,
                color: textSec,
                "&:hover": { backgroundColor: "rgba(29,158,117,0.08)", color: accent },
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: "15px" }} />
            </IconButton>
          </Box>

          <Field label="Business Name" value={business_name} textPri={textPri} textSec={textSec} />
          <Field label="Slogan"        value={business_slogan} textPri={textPri} textSec={textSec} />
          <Field label="Address"       value={address}        textPri={textPri} textSec={textSec} />
          <Field label="Email"         value={email}          textPri={textPri} textSec={textSec} />

          <FixedSizeDialog
            dialogHeading="Edit Business Details"
            open={openDetail}
            handleClickOpen={() => setOpenDetail(true)}
            handleClose={() => setOpenDetail(false)}
            negativeResponse="Cancel"
            maxWidth="md"
            content={
              <Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}>
                <BusinessDetailEditForm
                  editData={business}
                  setOpen={setOpenDetail}
                  setIsSuccess={setIsSuccess}
                  setNewBusinessData={setNewBusinessData}
                />
              </Box>
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default BusinessSetupViewForm;